import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Plus, Trash2, Upload, Loader2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { Invoice, InvoiceItem } from '../../types';
import { generateInvoicePDF } from '../../utils/pdfGenerator';
import { useAppStore } from '../../store';
import ErrorModal from './ErrorModal';
import { supabase } from '../../lib/supabase';
import { getCurrentArgentinaISOString, adjustDateForInput } from '../../utils/dateUtils';

interface InvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  entity_id: string;
  entity_type: 'client' | 'supplier';
  entityName: string;
}

const InvoiceModal: React.FC<InvoiceModalProps> = ({
  isOpen,
  onClose,
  entity_id,
  entity_type,
  entityName,
}) => {
  const { addInvoice, invoices } = useAppStore();
  const [items, setItems] = useState<Omit<InvoiceItem, 'id'>[]>([]);
  const [invoiceType, setInvoiceType] = useState<'A' | 'B' | 'C' | 'X'>('A');
  const [date, setDate] = useState(getCurrentArgentinaISOString());
  const [dueDate, setDueDate] = useState('');
  const [issued_in_afip, setIssuedInAfip] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [manualNumber, setManualNumber] = useState(false);
  const [reference, setReference] = useState('');
  const [invoiceFile, setInvoiceFile] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCheckingNumber, setIsCheckingNumber] = useState(false);
  const [salesperson, setSalesperson] = useState<'Mateo' | 'Male' | null>(null);
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setItems([]);
      setInvoiceType('A');
      setDate(getCurrentArgentinaISOString());
      setDueDate('');
      setIssuedInAfip(false);
      setInvoiceNumber('');
      setManualNumber(false);
      setReference('');
      setInvoiceFile(null);
      setSalesperson(null);
    }
  }, [isOpen]);
  
  const addItem = () => {
    const newItem: Omit<InvoiceItem, 'id'> = {
      description: '',
      quantity: 1,
      unit_price: 0,
      tax_rate: 21,
      total: 0,
    };
    setItems([...items, newItem]);
  };
  
  const updateItem = (index: number, field: keyof InvoiceItem, value: any) => {
    setItems(items.map((item, i) => {
      if (i === index) {
        const updatedItem = { ...item, [field]: value };
        // Recalculate total
        if (field === 'quantity' || field === 'unit_price' || field === 'tax_rate') {
          const quantity = Math.max(0, updatedItem.quantity);
          const unit_price = Math.max(0, updatedItem.unit_price);
          const tax_rate = Math.max(0, updatedItem.tax_rate);
          const subtotal = quantity * unit_price;
          updatedItem.total = subtotal * (1 + tax_rate / 100);
          updatedItem.quantity = quantity;
          updatedItem.unit_price = unit_price;
          updatedItem.tax_rate = tax_rate;
        }
        return updatedItem;
      }
      return item;
    }));
  };
  
  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };
  
  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const tax = items.reduce((sum, item) => {
      const itemSubtotal = item.quantity * item.unit_price;
      return sum + (itemSubtotal * (item.tax_rate / 100));
    }, 0);
    const total = subtotal + tax;
    
    return { subtotal, tax, total };
  };

  const handleFileUpload = async (file: File) => {
    try {
      setIsUploading(true);

      // Create a unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `invoices/${fileName}`;

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('assets')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('assets')
        .getPublicUrl(filePath);

      setInvoiceFile(publicUrl);
    } catch (error: any) {
      console.error('Error uploading file:', error);
      setError(error.message || 'Error al subir el archivo');
      setShowErrorModal(true);
    } finally {
      setIsUploading(false);
    }
  };

  // Check if invoice number already exists
  const checkInvoiceNumber = (number: string): boolean => {
    return invoices.some(invoice => 
      invoice.number.toLowerCase() === number.toLowerCase()
    );
  };

  // Generate a unique invoice number
  const generateUniqueInvoiceNumber = (): string => {
    const prefix = invoiceType;
    let counter = 1;
    let candidateNumber = `${prefix}-${counter.toString().padStart(8, '0')}`;
    
    while (checkInvoiceNumber(candidateNumber)) {
      counter++;
      candidateNumber = `${prefix}-${counter.toString().padStart(8, '0')}`;
    }
    
    return candidateNumber;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (items.length === 0) {
      setError('Debe agregar al menos un ítem a la factura');
      setShowErrorModal(true);
      return;
    }
    
    const { subtotal, tax, total } = calculateTotals();
    
    // Ensure all amounts are non-negative
    if (subtotal < 0 || tax < 0 || total < 0) {
      setError('Los montos no pueden ser negativos');
      setShowErrorModal(true);
      return;
    }

    // Check if manual number is provided and validate it
    if (manualNumber) {
      if (!invoiceNumber.trim()) {
        setError('Debe ingresar un número de factura');
        setShowErrorModal(true);
        return;
      }

      setIsCheckingNumber(true);
      const isDuplicate = checkInvoiceNumber(invoiceNumber);
      setIsCheckingNumber(false);

      if (isDuplicate) {
        setError('Ya existe una factura con este número. Por favor, ingrese un número diferente.');
        setShowErrorModal(true);
        return;
      }
    }

    // Use provided number or generate a unique one
    const finalInvoiceNumber = manualNumber ? invoiceNumber : generateUniqueInvoiceNumber();
    
    const invoice: Omit<Invoice, 'id'> = {
      number: finalInvoiceNumber,
      date,
      due_date: dueDate || null,
      entity_id,
      entity_type,
      type: invoiceType,
      items,
      subtotal,
      tax,
      total,
      balance: total,
      issued_in_afip,
      reference: reference || null,
      invoice_file: invoiceFile || null,
      salesperson: salesperson,
    };
    
    try {
      // Add invoice through store
      await addInvoice(invoice, items);
      
      // Generate PDF only for client invoices
// Generar PDF sólo si es de tipo X (hasta que adaptemos el generator)
if (entity_type === 'client' && invoiceType === 'X') {
  await generateInvoicePDF(invoice as Invoice, entityName);
}

      
      onClose();
    } catch (error: any) {
      if (error.message.includes('duplicate key value') && error.message.includes('invoices_number_key')) {
        setError('Ya existe una factura con este número. Por favor, ingrese un número diferente.');
      } else {
        setError(error.message || 'Error al crear la factura');
      }
      setShowErrorModal(true);
    }
  };
  
  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[800px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white p-6 shadow-lg overflow-y-auto">
            <Dialog.Title className="text-xl font-semibold mb-4">
              {entity_type === 'client' ? 'Nueva Factura' : 'Nueva Compra'}
            </Dialog.Title>
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Select
                  label="Tipo de Factura"
                  value={invoiceType}
                  onChange={(value) => setInvoiceType(value as 'A' | 'B' | 'C' | 'X')}
                  options={[
                    { value: 'A', label: 'Factura A' },
                    { value: 'B', label: 'Factura B' },
                    { value: 'C', label: 'Factura C' },
                    { value: 'X', label: 'Factura X' },
                  ]}
                />
                
                <Input
                  type="date"
                  label="Fecha"
                  id="date"
                  name="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
                
                <Input
                  type="date"
                  label="Fecha de Vencimiento"
                  id="dueDate"
                  name="dueDate"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
                
                {(entity_type === 'client' && (invoiceType === 'A' || invoiceType === 'B' || invoiceType === 'C')) && (
                  <div className="flex items-center mt-7">
                    <input
                      type="checkbox"
                      id="issued_in_afip"
                      checked={issued_in_afip}
                      onChange={(e) => setIssuedInAfip(e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="issued_in_afip" className="ml-2 block text-sm text-gray-900">
                      Emitida en AFIP
                    </label>
                  </div>
                )}
              </div>
              
              {/* Manual invoice number option */}
              <div className="mb-6">
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="manualNumber"
                    checked={manualNumber}
                    onChange={(e) => setManualNumber(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="manualNumber" className="ml-2 block text-sm text-gray-900">
                    Ingresar número de factura manualmente
                  </label>
                </div>
                
                {manualNumber && (
                  <Input
                    label="Número de Factura"
                    id="invoiceNumber"
                    name="invoiceNumber"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    required={manualNumber}
                    placeholder={`${invoiceType}-XXXXXXXX`}
                    error={isCheckingNumber ? "Verificando número..." : undefined}
                  />
                )}
              </div>

              {/* Reference field */}
              <div className="mb-6">
                <Input
                  label="Referencia (opcional)"
                  id="reference"
                  name="reference"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Número de orden, referencia interna, etc."
                />
              </div>

             {/* Salesperson field */}
             {entity_type === 'client' && (
               <div className="mb-6">
                 <Select
                   label="Vendedor (opcional)"
                   value={salesperson || ''}
                   onChange={(value) => setSalesperson(value ? value as 'Mateo' | 'Male' : null)}
                   options={[
                     { value: '', label: 'Ninguno' },
                     { value: 'Mateo', label: 'Mateo' },
                     { value: 'Male', label: 'Male' },
                   ]}
                 />
               </div>
             )}

              {/* File upload field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Archivo de Factura (opcional)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file);
                    }}
                    className="hidden"
                    id="invoice-file"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('invoice-file')?.click()}
                    disabled={isUploading}
                    icon={isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  >
                    Subir Archivo
                  </Button>
                  {invoiceFile && (
                    <a
                      href={invoiceFile}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-indigo-600 hover:text-indigo-500"
                    >
                      Ver archivo
                    </a>
                  )}
                </div>
              </div>
              
              {/* Items */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">Items</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    icon={<Plus className="h-4 w-4" />}
                    onClick={addItem}
                  >
                    Agregar Item
                  </Button>
                </div>
                
                {items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-4">
                      <Input
                        label="Descripción"
                        id={`desc-${index}`}
                        name={`desc-${index}`}
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        label="Cantidad"
                        type="number"
                        id={`qty-${index}`}
                        name={`qty-${index}`}
                        value={item.quantity.toString()}
                        onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value))}
                        min="0"
                        step="1"
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        label="Precio Unit."
                        type="number"
                        id={`price-${index}`}
                        name={`price-${index}`}
                        value={item.unit_price.toString()}
                        onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value))}
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        label="IVA %"
                        type="number"
                        id={`tax-${index}`}
                        name={`tax-${index}`}
                        value={item.tax_rate.toString()}
                        onChange={(e) => updateItem(index, 'tax_rate', parseFloat(e.target.value))}
                        min="0"
                        step="0.1"
                        required
                      />
                    </div>
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total
                      </label>
                      <p className="text-sm font-medium py-2">
                        ${item.total.toFixed(2)}
                      </p>
                    </div>
                    <div className="col-span-1">
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="mt-7 p-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Totals */}
              {items.length > 0 && (
                <div className="mt-6 border-t pt-4">
                  <div className="flex justify-end space-y-2">
                    <div className="w-48 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Subtotal:</span>
                        <span className="text-sm font-medium">
                          ${calculateTotals().subtotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">IVA:</span>
                        <span className="text-sm font-medium">
                          ${calculateTotals().tax.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="text-sm font-medium">Total:</span>
                        <span className="text-sm font-bold">
                          ${calculateTotals().total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mt-6 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={items.length === 0 || isUploading || isCheckingNumber}
                >
                  Guardar {entity_type === 'client' ? 'Factura' : 'Compra'}
                </Button>
              </div>
            </form>
            
            <Dialog.Close asChild>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                aria-label="Close"
              >
                <X className="h-6 w-6" />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />
    </>
  );
};

export default InvoiceModal;